import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Bell, Send, Users, Target, CheckCircle2, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Push-Broadcast Modul
 *
 * Ermöglicht Admins:
 * 1. Test-Push an sich selbst (als Admin-User) senden
 * 2. Broadcast an Zielgruppe (alle / aktive / inaktive / Custom-Filter)
 * 3. Sofortiges Feedback: wie viele empfangen haben / welche fehlgeschlagen
 *
 * Endpoint: POST /api/admin/firebase/send
 * Payload: { customer_ids: [...], notification: {...}, notification_type, priority }
 */
const PushBroadcastPanel = () => {
  const [title, setTitle] = useState('StrongFit 🏋️');
  const [body, setBody] = useState('');
  const [clickAction, setClickAction] = useState('');
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [tokenStats, setTokenStats] = useState(null);

  // Scheduled-Send
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');

  // Scheduled-Filter State
  const [filterMembership, setFilterMembership] = useState('');
  const [filterMinAge, setFilterMinAge] = useState('');
  const [filterMaxAge, setFilterMaxAge] = useState('');
  const [filterLastVisitDays, setFilterLastVisitDays] = useState('');
  const [filterLastVisitMode, setFilterLastVisitMode] = useState('had'); // had | missing
  const [filterDeviceType, setFilterDeviceType] = useState('any'); // any | ios | android
  const [audience, setAudience] = useState({ total_count: 0, customer_ids: [], preview: [] });
  const [resolvingAudience, setResolvingAudience] = useState(false);

  const token = () => localStorage.getItem('token');

  // Lade FCM-Token-Stats beim Öffnen
  useEffect(() => {
    const loadStats = async () => {
      try {
        const res = await axios.get(`${API}/admin/push-status/overview`, {
          headers: { Authorization: `Bearer ${token()}` },
        });
        setTokenStats(res.data?.totals || null);
      } catch (e) {
        // silent
      }
    };
    loadStats();
  }, []);

  // Resolve audience immer wenn Filter sich ändern (mit Debounce)
  useEffect(() => {
    const t = setTimeout(() => resolveAudience(), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMembership, filterMinAge, filterMaxAge, filterLastVisitDays, filterLastVisitMode, filterDeviceType]);

  const resolveAudience = async () => {
    setResolvingAudience(true);
    try {
      const body = {
        only_with_fcm: true,
        ...(filterMembership.trim() && { membership_name: filterMembership.trim() }),
        ...(filterMinAge && { min_age: parseInt(filterMinAge, 10) }),
        ...(filterMaxAge && { max_age: parseInt(filterMaxAge, 10) }),
        ...(filterLastVisitDays && {
          last_visit_days: parseInt(filterLastVisitDays, 10),
          last_visit_days_inverse: filterLastVisitMode === 'missing',
        }),
        ...(filterDeviceType !== 'any' && { device_type: filterDeviceType }),
      };
      const res = await axios.post(`${API}/admin/firebase/resolve-audience`, body, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setAudience(res.data);
    } catch (e) {
      // silent
    } finally {
      setResolvingAudience(false);
    }
  };

  /**
   * Schickt Push an Zielgruppe. Wenn `isTest`, nur an den ersten FCM-Token.
   * Wenn `scheduleMode=true`, wird der Broadcast geplant statt sofort gesendet.
   */
  const sendPush = async ({ isTest = false } = {}) => {
    if (!isTest && (!title.trim() || !body.trim())) {
      toast.error('Titel und Nachricht sind Pflicht');
      return;
    }
    setSending(true);
    setLastResult(null);
    try {
      if (isTest) {
        // Korrekt: Test an die EIGENEN registrierten Admin-Geräte (JWT → user_id),
        // nicht mehr an einen zufälligen Kunden-Token (Legacy /firebase/send-test).
        const res = await axios.post(
          `${API}/admin/push-devices/test`,
          { scope: 'mine' },
          { headers: { Authorization: `Bearer ${token()}` } }
        );
        const { sent = 0, fcm_available = true } = res.data || {};
        setLastResult({ test: true, ...res.data });
        if (sent > 0) {
          toast.success(`🧪 Test-Push an ${sent} ${sent === 1 ? 'dein Gerät' : 'deine Geräte'} gesendet`);
        } else if (!fcm_available) {
          toast.error('Push-Dienst (FCM) ist nicht konfiguriert.');
        } else {
          toast.error(
            'Kein registriertes Gerät gefunden. Öffne die StrongFit Admin-App auf deinem Handy und erlaube Benachrichtigungen – dann kommt der Test bei dir an. (Der Web-Admin selbst empfängt keine Push.)'
          );
        }
        return;
      }

      if (!audience.customer_ids || audience.customer_ids.length === 0) {
        toast.error('Zielgruppe ist leer. Bitte Filter anpassen.');
        return;
      }

      // Scheduled Broadcast?
      if (scheduleMode) {
        if (!scheduledAt) {
          toast.error('Bitte Zeitpunkt wählen');
          return;
        }
        // Local datetime-input → ISO; Browser parst es als Local-Zeit
        const scheduledDate = new Date(scheduledAt);
        if (!isFinite(scheduledDate.getTime()) || scheduledDate <= new Date()) {
          toast.error('Zeitpunkt muss in der Zukunft liegen');
          return;
        }
        const res = await axios.post(
          `${API}/admin/firebase/schedule`,
          {
            title: title.trim(),
            body: body.trim(),
            click_action: clickAction || null,
            scheduled_at: scheduledDate.toISOString(),
            audience_filter: { customer_ids: audience.customer_ids },
            notification_type: 'marketing_broadcast',
          },
          { headers: { Authorization: `Bearer ${token()}` } }
        );
        toast.success(
          `⏰ Broadcast geplant für ${scheduledDate.toLocaleString('de-DE')}`
        );
        setLastResult({
          scheduled: true,
          count: audience.customer_ids.length,
          at: scheduledDate.toISOString(),
        });
        // Reset
        setScheduledAt('');
        return;
      }

      // Sofort senden
      const payload = {
        customer_ids: audience.customer_ids,
        notification: {
          title: title.trim(),
          body: body.trim(),
          click_action: clickAction || undefined,
          sound: 'default',
        },
        notification_type: 'marketing_broadcast',
        priority: 'normal',
      };
      const res = await axios.post(`${API}/admin/firebase/send`, payload, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setLastResult(res.data);
      toast.success(
        `📱 ${res.data.sent_count || 0} Pushes gesendet, ${res.data.failed_count || 0} fehlgeschlagen`
      );
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error('Fehler: ' + msg);
      setLastResult({ error: msg });
    } finally {
      setSending(false);
    }
  };

  const resetFilters = () => {
    setFilterMembership('');
    setFilterMinAge('');
    setFilterMaxAge('');
    setFilterLastVisitDays('');
    setFilterLastVisitMode('had');
    setFilterDeviceType('any');
  };

  return (
    <div className="space-y-4" data-testid="push-broadcast-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Bell className="w-5 h-5 text-amber-600" />
            Push-Broadcast
          </h3>
          <p className="text-sm text-gray-500">
            Sende Push-Benachrichtigungen an deine Kunden (Marketing, News, Aktionen).
          </p>
        </div>
        {tokenStats && (
          <div className="text-right text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <p className="text-gray-600">Erreichbare Kunden</p>
            <p className="text-lg font-bold text-amber-700" data-testid="reachable-count">
              {tokenStats.with_app_installed || 0}
            </p>
            <p className="text-[10px] text-gray-500">
              von {tokenStats.total_customers || 0} total
            </p>
          </div>
        )}
      </div>

      {/* Form */}
      <div className="bg-white border rounded-lg p-4 space-y-4">
        <div className="space-y-2">
          <Label htmlFor="push-title">Titel</Label>
          <Input
            id="push-title"
            data-testid="push-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="z.B. StrongFit 🏋️ — Neuer Kurs!"
            maxLength={65}
          />
          <p className="text-xs text-gray-400">{title.length}/65 Zeichen</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="push-body">Nachricht</Label>
          <Textarea
            id="push-body"
            data-testid="push-body-input"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Kurze, ansprechende Nachricht (max. 240 Zeichen empfohlen)"
            rows={3}
            maxLength={240}
          />
          <p className="text-xs text-gray-400">{body.length}/240 Zeichen</p>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <div className="space-y-2">
            <Label htmlFor="click-action">Deep-Link (optional)</Label>
            <Input
              id="click-action"
              data-testid="push-click-action"
              value={clickAction}
              onChange={(e) => setClickAction(e.target.value)}
              placeholder="/bookings/new"
            />
          </div>
        </div>

        {/* Audience-Filter */}
        <div className="border rounded-lg p-3 bg-gray-50 space-y-3" data-testid="audience-filter">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-amber-700" />
              <p className="font-semibold text-sm">Zielgruppen-Filter</p>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded ${
                  resolvingAudience ? 'bg-gray-200 text-gray-500' : 'bg-amber-100 text-amber-800'
                }`}
                data-testid="audience-count"
              >
                {resolvingAudience ? '…' : audience.total_count} Empfänger
              </span>
              <button
                type="button"
                className="text-xs text-gray-500 hover:text-gray-800 underline"
                onClick={resetFilters}
                data-testid="reset-filters-btn"
              >
                Reset
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Mitgliedschaft (enthält)</Label>
              <Input
                value={filterMembership}
                onChange={(e) => setFilterMembership(e.target.value)}
                placeholder="z.B. EMS, Personal, Flex"
                className="h-9"
                data-testid="filter-membership"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Alter min</Label>
                <Input
                  type="number"
                  min="0"
                  max="120"
                  value={filterMinAge}
                  onChange={(e) => setFilterMinAge(e.target.value)}
                  placeholder="z.B. 30"
                  className="h-9"
                  data-testid="filter-min-age"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Alter max</Label>
                <Input
                  type="number"
                  min="0"
                  max="120"
                  value={filterMaxAge}
                  onChange={(e) => setFilterMaxAge(e.target.value)}
                  placeholder="z.B. 55"
                  className="h-9"
                  data-testid="filter-max-age"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Gerät</Label>
              <Select value={filterDeviceType} onValueChange={setFilterDeviceType}>
                <SelectTrigger className="h-9" data-testid="filter-device">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Alle</SelectItem>
                  <SelectItem value="ios">Nur iOS</SelectItem>
                  <SelectItem value="android">Nur Android</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1 col-span-full">
              <Label className="text-xs">Letzter Besuch</Label>
              <div className="flex items-center gap-2">
                <Select value={filterLastVisitMode} onValueChange={setFilterLastVisitMode}>
                  <SelectTrigger className="h-9 w-40" data-testid="filter-visit-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="had">War in den letzten…</SelectItem>
                    <SelectItem value="missing">Seit X Tagen nicht…</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  min="1"
                  max="365"
                  value={filterLastVisitDays}
                  onChange={(e) => setFilterLastVisitDays(e.target.value)}
                  placeholder="leer = egal"
                  className="h-9 w-32"
                  data-testid="filter-visit-days"
                />
                <span className="text-xs text-gray-500">Tagen</span>
              </div>
            </div>
          </div>

          {audience.preview && audience.preview.length > 0 && (
            <details className="text-xs text-gray-600 border-t pt-2">
              <summary className="cursor-pointer font-semibold">
                Vorschau: erste {audience.preview.length} Empfänger
              </summary>
              <div className="mt-2 flex flex-wrap gap-1" data-testid="audience-preview">
                {audience.preview.map((c) => (
                  <span
                    key={c.id}
                    className="bg-white border px-2 py-0.5 rounded text-gray-700"
                  >
                    {c.name || c.id.slice(0, 6)}
                  </span>
                ))}
              </div>
            </details>
          )}
        </div>

        {/* Preview */}
        {(title || body) && (
          <div className="rounded-lg bg-gray-900 text-white p-3">
            <p className="text-xs text-gray-400 mb-2 uppercase tracking-wide">Vorschau iOS/Android</p>
            <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-md bg-amber-500 flex items-center justify-center flex-shrink-0">
                  <Bell className="w-4 h-4 text-black" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{title || '(Titel fehlt)'}</p>
                  <p className="text-xs text-gray-300 line-clamp-2">{body || '(Nachricht fehlt)'}</p>
                </div>
                <span className="text-[10px] text-gray-500">jetzt</span>
              </div>
            </div>
          </div>
        )}

        {/* Schedule-Mode Toggle */}
        <div className="border-t pt-3">
          <div className="flex items-center gap-3">
            <input
              id="schedule-mode"
              type="checkbox"
              checked={scheduleMode}
              onChange={(e) => setScheduleMode(e.target.checked)}
              data-testid="schedule-mode-toggle"
              className="h-4 w-4"
            />
            <Label htmlFor="schedule-mode" className="cursor-pointer text-sm">
              ⏰ Broadcast planen (statt sofort senden)
            </Label>
          </div>
          {scheduleMode && (
            <div className="mt-2">
              <Input
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                data-testid="schedule-datetime"
                className="h-9 w-full md:w-80"
              />
              <p className="text-xs text-gray-500 mt-1">
                Der Broadcast wird automatisch zum gewählten Zeitpunkt gesendet
                (Check alle 5 Minuten).
              </p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
          <Button
            data-testid="send-test-push-btn"
            variant="outline"
            onClick={() => sendPush({ isTest: true })}
            disabled={sending}
          >
            <Target className="w-4 h-4 mr-2" />
            Test an mich selbst
          </Button>
          <Button
            data-testid="send-broadcast-btn"
            className="bg-amber-500 hover:bg-amber-600 text-black"
            onClick={() => {
              const count = audience.total_count || 0;
              if (count === 0) {
                toast.error('Zielgruppe ist leer');
                return;
              }
              const msg = scheduleMode
                ? `Broadcast an ${count} Kunden planen für ${scheduledAt}?\n\nTitel: ${title}\nNachricht: ${body}`
                : `Push an ${count} Kunden senden?\n\nTitel: ${title}\nNachricht: ${body}`;
              if (!window.confirm(msg)) return;
              sendPush();
            }}
            disabled={sending || !title.trim() || !body.trim() || audience.total_count === 0 || (scheduleMode && !scheduledAt)}
          >
            <Send className="w-4 h-4 mr-2" />
            {sending
              ? 'Sende…'
              : scheduleMode
                ? `Planen (${audience.total_count})`
                : `An ${audience.total_count} senden`}
          </Button>
        </div>

        {/* Result */}
        {lastResult && (
          <div className={`rounded-lg p-3 text-sm ${lastResult.error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`} data-testid="push-last-result">
            {lastResult.error ? (
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                <span>{lastResult.error}</span>
              </div>
            ) : lastResult.test ? (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                <span>{lastResult.message || 'Test gesendet'}</span>
              </div>
            ) : lastResult.scheduled ? (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                <span>
                  ⏰ Geplant für {new Date(lastResult.at).toLocaleString('de-DE')} ·
                  {' '}{lastResult.count} Empfänger
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                <span>
                  Gesendet: {lastResult.sent_count || 0} · Fehlgeschlagen: {lastResult.failed_count || 0}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PushBroadcastPanel;
